import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import Header from '../../layout/Header';
import Subheader from '../../layout/Subheader';
import Loading from '../../components/Loading';
import {
  consultarHangfireStatus,
  triggerRecurringJob,
  reprogramarRecurringJob,
} from '../../services/monitorHangfireService';
import useStyles from './style';

const REFRESH_MS = 15000;

const STATE_COLORS = {
  Succeeded: '#4CAF50',
  Failed: '#F44336',
  Processing: '#2196F3',
  Enqueued: '#FF9800',
  Scheduled: '#9C27B0',
  Deleted: '#9E9E9E',
  Awaiting: '#607D8B',
};

function stateColor(state) {
  return STATE_COLORS[state] || '#607D8B';
}

function formatDateTime(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('pt-BR');
}

function fromNow(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Math.round((d.getTime() - Date.now()) / 1000);
  const abs = Math.abs(diff);
  const pref = diff >= 0 ? 'em ' : 'há ';
  if (abs < 60) return `${pref}${abs}s`;
  if (abs < 3600) return `${pref}${Math.round(abs / 60)}min`;
  if (abs < 86400) return `${pref}${Math.round(abs / 3600)}h`;
  return `${pref}${Math.round(abs / 86400)}d`;
}

function methodFromInvocation(invocation) {
  if (!invocation) return '';
  try {
    const parsed = JSON.parse(invocation);
    return parsed.m || '';
  } catch {
    return invocation.slice(0, 60);
  }
}

export function MonitorHangfire() {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [detailJob, setDetailJob] = useState(null);
  const [triggeringJobId, setTriggeringJobId] = useState(null);
  const [reprogramJob, setReprogramJob] = useState(null); // { id, horarioAtual, tipo }
  const [reprogramHorario, setReprogramHorario] = useState('');
  const [reprogramSaving, setReprogramSaving] = useState(false);
  const timerRef = useRef(null);

  const handleTrigger = useCallback(
    async (job) => {
      const nome = job.vinculoApelido || job.id;
      const confirmar = window.confirm(`Executar agora "${nome}"? Isso ira disparar imediatamente sem esperar o horario.`);
      if (!confirmar) return;
      setTriggeringJobId(job.id);
      try {
        await triggerRecurringJob(job.id);
        toast.success(`Job "${nome}" enfileirado.`);
      } catch (err) {
        toast.error(err?.response?.data?.message || 'Falha ao disparar o job.');
      } finally {
        setTriggeringJobId(null);
      }
    },
    [],
  );

  const openReprogramar = useCallback((job) => {
    setReprogramJob(job);
    setReprogramHorario(job.horarioAtual || '');
  }, []);

  const salvarReprogramacao = useCallback(async () => {
    if (!reprogramJob) return;
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(reprogramHorario)) {
      toast.warn('Informe horario no formato HH:mm (00:00 a 23:59).');
      return;
    }
    setReprogramSaving(true);
    try {
      await reprogramarRecurringJob(reprogramJob.id, reprogramHorario);
      toast.success(`Horario de "${reprogramJob.vinculoApelido || reprogramJob.id}" atualizado para ${reprogramHorario}.`);
      setReprogramJob(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Falha ao reprogramar.');
    } finally {
      setReprogramSaving(false);
    }
  }, [reprogramJob, reprogramHorario]);

  const fetchStatus = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await consultarHangfireStatus(50);
      const payload = res?.data ?? res;
      setData(payload);
      setError(null);
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.message || 'Falha desconhecida.';
      setError(status ? `HTTP ${status}: ${msg}` : msg);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus(true);
  }, [fetchStatus]);

  useEffect(() => {
    if (!autoRefresh) {
      if (timerRef.current) clearInterval(timerRef.current);
      return undefined;
    }
    timerRef.current = setInterval(() => fetchStatus(false), REFRESH_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [autoRefresh, fetchStatus]);

  return (
    <div className={classes.root}>
      <Header />
      <Subheader title='Monitor Hangfire' back='/app/home' />
      <div className={classes.content}>
        <div className={classes.container}>
          <div className={classes.actionsBar}>
            <div className={classes.refreshBadge}>
              {autoRefresh ? `Auto-refresh a cada ${REFRESH_MS / 1000}s` : 'Auto-refresh pausado'}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type='button'
                className={classes.detailBtn}
                onClick={() => setAutoRefresh((v) => !v)}
              >
                {autoRefresh ? 'Pausar' : 'Retomar'}
              </button>
              <button
                type='button'
                className={classes.detailBtn}
                onClick={() => fetchStatus(true)}
              >
                Atualizar agora
              </button>
            </div>
          </div>

          {loading && <Loading />}

          {error && !loading && (
            <div className={classes.errorBox}>
              <b>Falha ao consultar:</b> {error}
            </div>
          )}

          {data && !loading && (
            <>
              <div className={classes.section}>
                <div className={classes.sectionTitle}>
                  Servers ({data.servers?.length || 0})
                </div>
                {(!data.servers || data.servers.length === 0) && (
                  <div style={{ color: '#a94442', fontSize: 13 }}>
                    Nenhum servidor Hangfire ativo! Jobs agendados não vão rodar.
                  </div>
                )}
                {data.servers?.map((s) => {
                  let parsedData = {};
                  try {
                    parsedData = JSON.parse(s.data || '{}');
                  } catch {
                    /* ignore */
                  }
                  return (
                    <div key={s.id} style={{ marginBottom: 6, fontSize: 13 }}>
                      <div>
                        <b>{s.id}</b>
                      </div>
                      <div style={{ color: '#666', fontSize: 12 }}>
                        Heartbeat: {formatDateTime(s.lastHeartbeat)} ({fromNow(s.lastHeartbeat)}) ·
                        Workers: {parsedData.WorkerCount || '?'} ·
                        Queues: {(parsedData.Queues || []).join(', ')}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className={classes.section}>
                <div className={classes.sectionTitle}>Contadores por estado</div>
                <div className={classes.countsGrid}>
                  {Object.entries(data.counts || {}).map(([state, qtd]) => (
                    <span
                      key={state}
                      className={classes.countChip}
                      style={{ background: stateColor(state) }}
                    >
                      {state}: {qtd}
                    </span>
                  ))}
                  {(!data.counts || Object.keys(data.counts).length === 0) && (
                    <span style={{ color: '#999', fontSize: 13 }}>Sem jobs registrados.</span>
                  )}
                </div>
              </div>

              <div className={classes.section}>
                <div className={classes.sectionTitle}>
                  Recurring Jobs ({data.recurringJobs?.length || 0})
                </div>
                {(!data.recurringJobs || data.recurringJobs.length === 0) && (
                  <div style={{ color: '#999', fontSize: 13 }}>Nenhum recurring job registrado.</div>
                )}
                {data.recurringJobs?.length > 0 && (
                  <table className={classes.table}>
                    <thead>
                      <tr>
                        <th className={classes.th}>Job</th>
                        <th className={classes.th}>Rede / Vínculo</th>
                        <th className={classes.th}>Tipo</th>
                        <th className={classes.th}>Horário</th>
                        <th className={classes.th}>Última</th>
                        <th className={classes.th}>Próxima</th>
                        <th className={classes.th}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recurringJobs.map((rj) => {
                        const isBluesoft = rj.tipo && rj.ancoraId;
                        const isReprogramavel = rj.horarioConfiguravel;
                        return (
                          <tr key={rj.id}>
                            <td className={classes.td}>
                              <div><b>{rj.id}</b></div>
                              <div className={classes.monoSmall}>
                                {rj.cron} · {rj.timeZoneId || '-'}
                              </div>
                            </td>
                            <td className={classes.td}>
                              {isBluesoft ? (
                                <>
                                  <div><b>{rj.ancoraApelido}</b> (#{rj.ancoraId})</div>
                                  <div style={{ fontSize: 11, color: '#666' }}>{rj.vinculoApelido}</div>
                                  {rj.vinculoAtivo === false && (
                                    <div style={{ fontSize: 10, color: '#F44336' }}>Vínculo Inativo</div>
                                  )}
                                </>
                              ) : (
                                <span style={{ color: '#999' }}>—</span>
                              )}
                            </td>
                            <td className={classes.td}>
                              {rj.tipo ? (
                                <span
                                  className={classes.stateChip}
                                  style={{
                                    background:
                                      rj.tipo === 'Full'
                                        ? '#2196F3'
                                        : rj.tipo === 'Complementar'
                                        ? '#9C27B0'
                                        : '#607D8B',
                                  }}
                                >
                                  {rj.tipo}
                                </span>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className={classes.td} style={{ fontFamily: 'monospace' }}>
                              {rj.horarioAtual || '-'}
                            </td>
                            <td className={classes.td}>
                              {formatDateTime(rj.lastExecution)}
                              <div style={{ fontSize: 11, color: '#666' }}>
                                {fromNow(rj.lastExecution)}
                              </div>
                            </td>
                            <td className={classes.td}>
                              {formatDateTime(rj.nextExecution)}
                              <div style={{ fontSize: 11, color: '#666' }}>
                                {fromNow(rj.nextExecution)}
                              </div>
                            </td>
                            <td className={classes.td}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {isBluesoft && (
                                  <Link
                                    to={`/app/editar-ancora/${rj.ancoraId}`}
                                    className={classes.detailBtn}
                                    style={{ textAlign: 'center', textDecoration: 'none' }}
                                  >
                                    Ver Config
                                  </Link>
                                )}
                                <button
                                  type='button'
                                  className={classes.detailBtn}
                                  onClick={() => handleTrigger(rj)}
                                  disabled={triggeringJobId === rj.id}
                                >
                                  {triggeringJobId === rj.id ? '...' : 'Executar agora'}
                                </button>
                                {isReprogramavel && (
                                  <button
                                    type='button'
                                    className={classes.detailBtn}
                                    onClick={() => openReprogramar(rj)}
                                  >
                                    Reprogramar
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              <div className={classes.section}>
                <div className={classes.sectionTitle}>
                  Jobs recentes ({data.recentJobs?.length || 0})
                </div>
                {(!data.recentJobs || data.recentJobs.length === 0) && (
                  <div style={{ color: '#999', fontSize: 13 }}>Nenhum job registrado ainda.</div>
                )}
                {data.recentJobs?.length > 0 && (
                  <table className={classes.table}>
                    <thead>
                      <tr>
                        <th className={classes.th}>Id</th>
                        <th className={classes.th}>Estado</th>
                        <th className={classes.th}>Criado em</th>
                        <th className={classes.th}>Método</th>
                        <th className={classes.th}>Motivo (se falhou)</th>
                        <th className={classes.th}>Detalhes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentJobs.map((j) => (
                        <tr key={j.id}>
                          <td className={classes.td}>{j.id}</td>
                          <td className={classes.td}>
                            <span
                              className={classes.stateChip}
                              style={{ background: stateColor(j.stateName) }}
                            >
                              {j.stateName || '-'}
                            </span>
                          </td>
                          <td className={classes.td}>
                            {formatDateTime(j.createdAt)}
                            <div style={{ fontSize: 11, color: '#666' }}>
                              {fromNow(j.createdAt)}
                            </div>
                          </td>
                          <td className={classes.td} style={{ fontSize: 12 }}>
                            {methodFromInvocation(j.invocation)}
                          </td>
                          <td className={classes.td} style={{ fontSize: 12, color: '#A94442' }}>
                            {j.reason ? j.reason.substring(0, 120) : '-'}
                          </td>
                          <td className={classes.td}>
                            <button
                              type='button'
                              className={classes.detailBtn}
                              onClick={() => setDetailJob(j)}
                            >
                              Ver
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {detailJob && (
            <div className={classes.modalBackdrop} onClick={() => setDetailJob(null)}>
              <div
                className={classes.modalContent}
                onClick={(e) => e.stopPropagation()}
              >
                <div className={classes.modalTitle}>Job #{detailJob.id} — {detailJob.stateName}</div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                  Criado em {formatDateTime(detailJob.createdAt)} · Expira em{' '}
                  {formatDateTime(detailJob.expireAt)}
                </div>
                <div style={{ fontWeight: 'bold', fontSize: 13, marginBottom: 4 }}>
                  Invocation
                </div>
                <pre className={classes.preBlock}>{detailJob.invocation || '(vazio)'}</pre>
                <div style={{ fontWeight: 'bold', fontSize: 13, margin: '12px 0 4px' }}>
                  Reason
                </div>
                <pre className={classes.preBlock}>{detailJob.reason || '(vazio)'}</pre>
                <div style={{ fontWeight: 'bold', fontSize: 13, margin: '12px 0 4px' }}>
                  State Data (JSON com ExceptionDetails, etc.)
                </div>
                <pre className={classes.preBlock}>{detailJob.stateData || '(vazio)'}</pre>
                <button
                  type='button'
                  className={classes.closeBtn}
                  onClick={() => setDetailJob(null)}
                >
                  Fechar
                </button>
              </div>
            </div>
          )}

          {reprogramJob && (
            <div className={classes.modalBackdrop} onClick={() => setReprogramJob(null)}>
              <div
                className={classes.modalContent}
                onClick={(e) => e.stopPropagation()}
                style={{ width: 480 }}
              >
                <div className={classes.modalTitle}>Reprogramar {reprogramJob.tipo}</div>
                <div style={{ fontSize: 13, marginBottom: 12 }}>
                  {reprogramJob.ancoraId ? (
                    <>
                      <div><b>Rede:</b> {reprogramJob.ancoraApelido} (#{reprogramJob.ancoraId})</div>
                      <div><b>Vínculo:</b> {reprogramJob.vinculoApelido}</div>
                    </>
                  ) : (
                    <div><b>Job:</b> {reprogramJob.id}</div>
                  )}
                  <div><b>Horário atual:</b> {reprogramJob.horarioAtual || '(nenhum)'}</div>
                </div>
                <label style={{ fontSize: 13, fontWeight: 'bold', display: 'block', marginBottom: 4 }}>
                  Novo horário (HH:mm)
                </label>
                <input
                  type='time'
                  value={reprogramHorario}
                  onChange={(e) => setReprogramHorario(e.target.value)}
                  style={{
                    fontSize: 15,
                    padding: '8px 10px',
                    width: '100%',
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    boxSizing: 'border-box',
                  }}
                  disabled={reprogramSaving}
                />
                <div style={{ fontSize: 11, color: '#666', marginTop: 6 }}>
                  Atualiza o horário no vínculo e força a reconciliação do Hangfire — o novo cron passa a valer imediatamente.
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                  <button
                    type='button'
                    className={classes.detailBtn}
                    onClick={() => setReprogramJob(null)}
                    disabled={reprogramSaving}
                  >
                    Cancelar
                  </button>
                  <button
                    type='button'
                    className={classes.closeBtn}
                    onClick={salvarReprogramacao}
                    disabled={reprogramSaving}
                    style={{ marginTop: 0 }}
                  >
                    {reprogramSaving ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
